import { v4 as uuidv4 } from 'uuid';
import dayjs from 'utils/dayjs';
import { getDatesArrayForRange } from 'utils/dates';

import {
    UPDATE_OCCUPIED_COST_ROOM_BREAKDOWN_VALUE,
    UPDATE_OCCUPIED_COST_FORM_VALUE,
    UPDATE_OCCUPIED_COST_SECTION,
    UPDATE_PACKAGE_CALCULATOR_FORM_VALUE,
    UPDATE_PACKAGE_CALCULATOR_INCLUSIONS_VALUE,
    UPDATE_PACKAGE_CALCULATOR_INCREMENTS,
    UPDATE_GROUP_DISPLACEMENT_FORM_VALUE,
    UPDATE_GROUP_DISPLACEMENT_ROOM_NUMBER,
    UPDATE_GROUP_DISPLACEMENT_ADR_DATA,
    UPDATE_GROUP_DISPLACEMENT_FORECAST_DATA,
    UPDATE_GROUP_DISPLACEMENT_ALL_FORECASTS,
    UPDATE_GROUP_DISPLACEMENT_ADDITIONAL_SPEND,
    GROUP_DISPLACEMENT_CALCULATE_MIN_RATE,
    UPDATE_GROUP_DISPLACEMENT_ADDITIONAL_SPEND_SECTION,
    GROUP_DISPLACEMENT_RESET_FORECASTS,
    UPDATE_GROUP_DISPLACEMENT_NORMAL_SPEND,
    LOAD_SAVE_START,
    LOAD_SAVE_SUCCESS,
    LOAD_SAVE_ERROR,
    RESET_LOADED_SAVE_STATE,
    RESET_FORM,
    FETCH_SAVES_START,
    FETCH_SAVES_SUCCESS,
    FETCH_SAVES_ERROR,
    RESET_SAVES_STATE,
    SAVE_FORM_START,
    SAVE_FORM_SUCCESS,
    SAVE_FORM_ERROR,
    RESET_FROM_SAVING_STATE,
} from './constants';
import {
    updatePackageCalculatorInclusions,
    updatePackageCalculatorProposal,
    updatePackageCalculatorIncrements,
    calculateMinRate,
    generateRooms,
    updateADRData,
    updateForecastData,
    updateCORFormItem,
    updateCORSectionToggle,
    calculateAdditionalSpend,
    toggleAdditionalSpend,
    updateAllForecasts,
    calculateNormalSpend,
    updateCORRoomBreakdown,
} from './utils';

export const initialAPIState = {
    fetching: false,
    fetched: false,
    error: null,
    filters: {},
    data: null,
};

export const initialState = {
    occupiedCostCalculator: {
        roomBreakdown: {
            numberOfRooms: '0',
            daysOpenPerPeriod: '0',
            totalRoomCountPerPeriod: 0, // calculated
            averageOccupancy: '0',
            averageOccupiedRoomCountPerPeriod: 0, // calculated
        },
        sections: {
            // whether a section is enabled or not
            fixedCosts: true,
            roomConsumables: true,
            salaryCosts: true,
            commission: true,
            variables: true,
        },
        roomConsumables: [
            {
                // id for field identification, whether preset or custom.
                // An extra security step to ensure uniqueness is adding a suffix
                id: `${uuidv4()}Linen`,
                name: 'Linen',
                value: '0',
                percentage: 100, // this is not a variable for consumable, always 100%
                // calculated
                cpor: 0, // cost per occupied room, e.g. value divided by averageOccupiedRoomCountPerPeriod
                applied: true, // rooms consumables are always applied (unless the section isn't), no individual toggle
                isPreset: true, // to differenciate the 3 presets vs custom fields, if we wish to not make them editable/deletable
            },
            {
                id: `${uuidv4()}Toiletries`,
                name: 'Toiletries',
                value: '0',
                percentage: 100,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}Incidentals`,
                name: 'Incidentals',
                value: '0',
                percentage: 100,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
        ],
        fixedCosts: [
            {
                // id for field identification, whether preset or custom.
                // An extra security step to ensure uniqueness is adding a suffix
                id: `${uuidv4()}Rent`,
                name: 'Rent',
                value: '0',
                percentage: 100,
                // calculated
                amount: 0, // percentage applied to value
                cpor: 0, // cost per occupied room, e.g. value divided by averageOccupiedRoomCountPerPeriod
                applied: true, // if the field is enabled/toggled on
                isPreset: true, // to differenciate the 3 presets vs custom fields, if we wish to not make them editable/deletable
            },
            {
                id: `${uuidv4()}Rates`,
                name: 'Rates',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}Insurance`,
                name: 'Insurance',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}Electricity`,
                name: 'Electricity',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}Water`,
                name: 'Water',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}Heating`,
                name: 'Heating',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}BankCharges`,
                name: 'Bank Charges',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}Maintenance`,
                name: 'Maintenance',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
        ],
        salaryCosts: [
            // see fixedCost for field explanation
            {
                id: `${uuidv4()}FTReception`,
                name: 'Full Time Reception',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}FTHouseKeeping`,
                name: 'Full Time House Keeping',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}PTReception`,
                name: 'Part Time Reception',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}PRHouseKeeping`,
                name: 'Part Time House Keeping',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
        ],
        commission: [
            // see fixedCost for field explanation
            {
                id: `${uuidv4()}Brand`,
                name: 'Brand',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}3rdParty`,
                name: '3rd Party',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}GDS`,
                name: 'GDS',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
        ],
        variables: [
            // see fixedCost for field explanation
            {
                id: `${uuidv4()}SalesSalary`,
                name: 'Sales Salary',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}SalesActivity`,
                name: 'Sales Activity',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}MarketingActivity`,
                name: 'Marketing Activity',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}MarketingSalary`,
                name: 'Marketing Salary',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}DigitalMarketing`,
                name: 'Digital Marketing',
                value: '0',
                percentage: 100,
                amount: 0,
                cpor: 0,
                applied: true,
                isPreset: true,
            },
        ],
        // cost per occupied room (per section)
        cpor: {
            fixedCosts: 0,
            roomConsumables: 0,
            salaryCosts: 0,
            commission: 0,
            variables: 0,
        },
    },
    packageCalculator: {
        // Proposals
        rateTitle: '',
        rateDescription: '',
        validFrom: dayjs().format('YYYY-MM-DD'),
        validTo: dayjs().format('YYYY-MM-DD'),
        releaseDate: dayjs().format('YYYY-MM-DD'),
        proposedRate: '0',
        numberOfNights: 0,
        numberOfAdults: 0,
        numberOfChildren: 0,
        numberOfInfants: 0,
        vat: '0',
        vatAmount: 0, // calculated
        commissionApplied: false,
        commissionBrand: '',
        commissionPercentage: '0',
        commissionAmount: 0, // calculated
        // Inclusions
        inclusions: [
            {
                // id for field identification, whether preset or custom.
                // An extra security step to ensure uniqueness is adding a suffix
                id: `${uuidv4()}breakfast`,
                // inputs
                name: 'Breakfast',
                value: '0', // gross value
                pricePer: 'perAdultPerNight',
                // calculated
                amount: 0, // net profit
                applied: true, // legacy, for when fields could be toggled
                isPreset: true, // to differenciate the presets vs custom fields, if we wish to not make them editable
                // other calculated field may be added
            },
            {
                id: `${uuidv4()}dinner`,
                name: 'Dinner',
                value: '0',
                pricePer: 'perAdultPerNight',
                amount: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}wine`,
                name: 'Wine',
                value: '0',
                pricePer: 'perItem',
                amount: 0,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}flowers`,
                name: 'Flowers',
                value: '0',
                pricePer: 'perItem',
                amount: 0,
                applied: true,
                isPreset: true,
            },
        ],
        // Totals & Cost of occupied room
        costOfOccupiedRoom: '0',
        totalCostOfOccupiedRoom: 0, // calculated - COR multiplied by number of nights
        totalReductions: 0, // calculated - total of all inclusions
        netProfit: 0, // calculated
        // Increments
        applyIncrements: false,
        incrementType: 'fixedAmount', // fixedAmount or percentage
        incrementDirection: 'increase', // increase or decrease
        incrementAmount: 0,
    },
    groupDisplacement: {
        groupName: '',
        startDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().add(1, 'days').format('YYYY-MM-DD'),
        groupBudget: '0',
        groupWash: false,
        groupWashPercent: '0',
        groupCommission: false,
        groupCommissionPercent: '0',
        // STEP 1 - hotel behaviour
        roomTypes: [
            {
                id: 0,
                name: 'WholeHouse',
                roomType: 'Whole House',
                // inputs
                hotelSize: '0',
                estADR: '0',
                occupiedRoomCost: '0',
                // calculated
                netADR: 0,
                expectedGroupWash: 0, // used for calculations only
            },
        ],
        averageNormalSpend: {
            // inputs
            gross: '0',
            percent: 100,
            // calculated
            net: 0,
        },
        // STEP 2 - Group forecast
        forecasts: [
            {
                name: 'Whole House',
                date: dayjs().format('YYYY-MM-DD'),
                // Inputs
                forecast: '0',
                groupSize: '0',
                compRooms: '0',
                // Calculated
                displacedRooms: 0,
                washRooms: 0, // used for calculations only
                expectedGroupWash: 0, // no longer used
            },
        ],
        // STEP 3 - Additional spend
        additionalSpend: [
            {
                // id for field identification, whether preset or custom.
                // An extra security step to ensure uniqueness is adding a suffix
                id: `${uuidv4()}rh`,
                // inputs
                name: 'Room Hire',
                value: '0', // gross value
                percentage: 100, // profit margin
                applyWash: false,
                applyCommission: false,
                // calculated
                amount: 0, // net profit
                applied: true, // legacy, for when fields could be toggled
                isPreset: true, // to differenciate the 3 presets vs custom fields, if we wish to not make them editable
                // other calculated field may be added
            },
            {
                id: `${uuidv4()}fb`,
                name: 'F & B',
                value: '0',
                percentage: 100,
                amount: 0,
                applyWash: false,
                applyCommission: false,
                applied: true,
                isPreset: true,
            },
            {
                id: `${uuidv4()}av`,
                name: 'AV',
                value: '0',
                percentage: 100,
                amount: 0,
                applyWash: false,
                applyCommission: false,
                applied: true,
                isPreset: true,
            },
        ],
        totalAdditionalSpend: 0, // calculated
        totalAdditionalSpendPerRoomPerNight: 0, // used for calculations only (?)
        summary: [
            {
                id: 'withoutGroup',
                rowHeader: 'Without Group',
                rooms: 0,
                revenue: 0,
                adr: 0,
                revPar: 0,
                additionalSpend: 0,
                trevPar: 0,
                gross: 0,
            },
            {
                id: 'withGroup',
                rowHeader: 'With Group',
                rooms: 0,
                revenue: 0,
                adr: 0,
                revPar: 0,
                additionalSpend: 0,
                trevPar: 0,
                gross: 0,
            },
        ],
    },
    fetchedSaves: initialAPIState,
    loadedSave: initialAPIState,
    formSave: initialAPIState,
    currentSave: null,
};

function formsReducer(state = initialState, action) {
    const { fieldName, fieldId, actionType, value, sectionName } = action;
    switch (action.type) {
        case UPDATE_OCCUPIED_COST_ROOM_BREAKDOWN_VALUE:
            return updateCORRoomBreakdown(state, fieldName, value);
        case UPDATE_OCCUPIED_COST_FORM_VALUE:
            return updateCORFormItem(state, sectionName, fieldId, actionType, value);
        case UPDATE_OCCUPIED_COST_SECTION:
            return updateCORSectionToggle(state, sectionName, value);
        case UPDATE_GROUP_DISPLACEMENT_ADDITIONAL_SPEND_SECTION:
            return toggleAdditionalSpend(state, action.enabled);
        case UPDATE_GROUP_DISPLACEMENT_FORM_VALUE:
            return {
                ...state,
                groupDisplacement: {
                    ...state.groupDisplacement,
                    [fieldName]: action.value,
                },
            };
        case UPDATE_GROUP_DISPLACEMENT_ROOM_NUMBER:
            return generateRooms(state, action.value);
        case UPDATE_GROUP_DISPLACEMENT_ADR_DATA:
            return updateADRData(state, action.data);
        case UPDATE_GROUP_DISPLACEMENT_FORECAST_DATA:
            return updateForecastData(state, action.data, action.roomType);
        case UPDATE_GROUP_DISPLACEMENT_ALL_FORECASTS:
            return updateAllForecasts(state);
        case UPDATE_GROUP_DISPLACEMENT_ADDITIONAL_SPEND:
            return calculateAdditionalSpend({ ...state }, fieldId, actionType, value);
        case UPDATE_GROUP_DISPLACEMENT_NORMAL_SPEND:
            return calculateNormalSpend({ ...state }, actionType, value);
        case GROUP_DISPLACEMENT_CALCULATE_MIN_RATE:
            return calculateMinRate(state);
        case UPDATE_PACKAGE_CALCULATOR_FORM_VALUE:
            return updatePackageCalculatorProposal(state, fieldName, value);
        case UPDATE_PACKAGE_CALCULATOR_INCLUSIONS_VALUE:
            return updatePackageCalculatorInclusions(state, fieldId, actionType, value);
        case UPDATE_PACKAGE_CALCULATOR_INCREMENTS:
            return updatePackageCalculatorIncrements(state, fieldName, value);
        case GROUP_DISPLACEMENT_RESET_FORECASTS: {
            const startDate = dayjs(state.groupDisplacement.startDate);
            const endDate = dayjs(state.groupDisplacement.endDate);
            const lastNightOfStay = endDate.isAfter(startDate) ? endDate.subtract(1, 'day') : endDate;

            const stayDates = getDatesArrayForRange(startDate, lastNightOfStay);
            const forecasts = [];

            state.groupDisplacement.roomTypes.forEach((room) => {
                stayDates.forEach((stayDate) => {
                    forecasts.push({
                        name: room.roomType,
                        date: stayDate.format('YYYY-MM-DD'),
                        forecast: '0',
                        groupSize: '0',
                        compRooms: '0',
                        displacedRooms: 0,
                        expectedGroupWash: 0,
                    });
                });
            });

            return {
                ...state,
                groupDisplacement: {
                    ...state.groupDisplacement,
                    forecasts,
                },
            };
        }
        case RESET_FORM: {
            return initialState;
        }
        // SELECTED SAVE LOADING
        case LOAD_SAVE_START: {
            return {
                ...state,
                loadedSave: {
                    ...initialAPIState,
                    fetching: true,
                    filters: action.params,
                },
            };
        }
        case LOAD_SAVE_SUCCESS: {
            return {
                ...state,
                loadedSave: {
                    ...state.loadedSave,
                    fetching: false,
                    fetched: true,
                    data: action.data,
                },
                // keep track of selected save, even if another save could be loaded
                currentSave: action.data,
                // load the save content in the state
                ...action.data.data,
            };
        }
        case LOAD_SAVE_ERROR: {
            return {
                ...state,
                loadedSave: {
                    ...state.loadedSave,
                    fetching: false,
                    error: action.error,
                },
            };
        }
        case RESET_LOADED_SAVE_STATE: {
            return {
                ...state,
                loadedSave: initialAPIState,
            };
        }
        // SAVES LIST FETCHING
        case FETCH_SAVES_START: {
            return {
                ...state,
                fetchedSaves: {
                    ...initialAPIState,
                    fetching: true,
                    filters: action.params,
                },
            };
        }
        case FETCH_SAVES_SUCCESS: {
            return {
                ...state,
                fetchedSaves: {
                    ...state.fetchedSaves,
                    fetching: false,
                    fetched: true,
                    data: action.data,
                },
            };
        }
        case FETCH_SAVES_ERROR: {
            return {
                ...state,
                fetchedSaves: {
                    ...state.fetchedSaves,
                    fetching: false,
                    error: action.error,
                },
            };
        }
        case RESET_SAVES_STATE: {
            return {
                ...state,
                fetchedSaves: initialAPIState,
            };
        }
        // SAVING FORM
        case SAVE_FORM_START: {
            return {
                ...state,
                formSave: {
                    ...initialAPIState,
                    fetching: true,
                    filters: action.params,
                },
            };
        }
        case SAVE_FORM_SUCCESS: {
            return {
                ...state,
                formSave: {
                    ...state.formSave,
                    fetching: false,
                    fetched: true,
                    data: action.data,
                },
                currentSave: action.data,
            };
        }
        case SAVE_FORM_ERROR: {
            return {
                ...state,
                formSave: {
                    ...state.formSave,
                    fetching: false,
                    error: action.error,
                },
            };
        }
        case RESET_FROM_SAVING_STATE: {
            return {
                ...state,
                formSave: initialAPIState,
            };
        }
        default:
            return state;
    }
}

export default formsReducer;
